import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { cn } from '../../utils/cn';
import ServiceSelector from './components/ServiceSelector';
import ConsultantMatcher from './components/ConsultantMatcher';
import BookingCalendar from './components/BookingCalendar';
import BookingForm from './components/BookingForm';

import PostBookingWorkflow from './components/PostBookingWorkflow';
import IntelligentNotaryMatcher from './components/IntelligentNotaryMatcher';
import { notaryService } from '../../services/notaryService';
import appointmentService from '../../services/appointmentService';
import { sendAppointmentConfirmationEmail } from '../../services/resendEmailService';
import { scheduleAppointmentReminders } from '../../services/appointmentReminderService';


// ─── Stepper (matches professional-onboarding pattern) ────────────────────────
const STEPS = [
  { id: 'service',      shortLabel: 'Service',     icon: 'LayoutGrid' },
  { id: 'consultant',   shortLabel: 'Consultant',  icon: 'UserCheck' },
  { id: 'datetime',     shortLabel: 'Date & Heure', icon: 'CalendarDays' },
  { id: 'informations', shortLabel: 'Informations', icon: 'ClipboardList' },
];

const Stepper = ({ currentStep, steps }) => (
  <nav aria-label="Étapes de réservation" className="mb-0">
    <ol className="flex items-center">
      {steps?.map((step, idx) => {
        const stepNum = idx + 1;
        const isCompleted = stepNum < currentStep;
        const isActive = stepNum === currentStep;
        const isLast = idx === steps?.length - 1;

        return (
          <React.Fragment key={step?.id}>
            <li className="flex flex-col items-center relative">
              <div
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-full border-2 text-sm font-medium transition-all duration-300 z-10',
                  isCompleted && 'border-primary bg-primary text-primary-foreground',
                  isActive && 'border-primary bg-background text-primary shadow-sm ring-4 ring-primary/10',
                  !isCompleted && !isActive && 'border-border bg-background text-muted-foreground'
                )}
              >
                {isCompleted ? (
                  <Icon name="Check" size={15} />
                ) : (
                  <Icon name={step?.icon} size={14} />
                )}
              </div>
              <span
                className={cn(
                  'mt-2 text-[10px] font-medium tracking-wide uppercase whitespace-nowrap hidden sm:block',
                  isActive ? 'text-primary' : isCompleted ? 'text-primary/70' : 'text-muted-foreground'
                )}
              >
                {step?.shortLabel}
              </span>
            </li>
            {!isLast && (
              <div className="flex-1 mx-2 mt-[-18px] sm:mt-[-22px]">
                <div
                  className={cn(
                    'h-0.5 w-full transition-all duration-500',
                    stepNum < currentStep ? 'bg-primary' : 'bg-border'
                  )}
                />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </ol>
  </nav>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const ConsultationBookingHub = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const [selectedDateTime, setSelectedDateTime] = useState(null);
  const [bookingData, setBookingData] = useState({});
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [createdAppointment, setCreatedAppointment] = useState(null);

  const services = [
    {
      id: 'property_consultation',
      name: 'Consultation Immobilière',
      description: 'Analyse de marché, conseil en investissement, coordination de visites',
      icon: 'Building2',
      duration: 60,
      price: 'Gratuite',
      features: [
        'Analyse du marché immobilier',
        'Conseils en investissement',
        'Évaluation de propriétés',
        'Coordination de visites',
        'Négociation assistée'
      ]
    },
    {
      id: 'legal_consultation',
      name: 'Consultation Légale',
      description: 'Services notariés, documentation, conseil transactionnel',
      icon: 'Scale',
      duration: 45,
      price: '50,000 FCFA',
      features: [
        'Services notariés',
        'Vérification de documents',
        'Conseil transactionnel',
        'Assistance juridique',
        'Préparation d\'actes'
      ]
    }
  ];

  useEffect(() => {
    if (selectedService?.id === 'legal_consultation') {
      loadNotaries();
    }
  }, [selectedService]);

  const loadNotaries = async () => {
    try {
      setLoading(true);
      const data = await notaryService?.getAllNotaries({ is_verified: true });
      setConsultants(data);
    } catch (err) {
      setError('Erreur lors du chargement des notaires');
      console.error('Error loading notaries:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleServiceSelect = (service) => {
    setSelectedService(service);
    setCurrentStep(2);
  };

  const handleConsultantSelect = (consultant) => {
    setSelectedConsultant(consultant);
    setCurrentStep(3);
  };

  const handleDateTimeSelect = (dateTime) => {
    setSelectedDateTime(dateTime);
    setCurrentStep(4);
  };

  const handleFormSubmit = async (formData) => {
    try {
      setLoading(true);

      const appointmentData = {
        appointment_type: selectedService?.name,
        scheduled_date: selectedDateTime,
        duration_minutes: selectedService?.duration,
        notes: formData?.notes
          ? (selectedService?.id === 'property_consultation' && selectedConsultant
              ? `Consultant: ${selectedConsultant?.fullName || selectedConsultant?.name || ''} — ${formData?.notes}`
              : formData?.notes)
          : (selectedService?.id === 'property_consultation' && selectedConsultant
              ? `Consultant: ${selectedConsultant?.fullName || selectedConsultant?.name || ''}`
              : ''),
        client_notes: formData?.preparation || '',
        location: selectedService?.id === 'legal_consultation'
          ? selectedConsultant?.office_address
          : 'Consultation en ligne',
        notary_id: selectedService?.id === 'legal_consultation'
          ? selectedConsultant?.id
          : null,
        agent_id: null,
      };

      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

      if (appointmentData?.agent_id && !uuidRegex?.test(appointmentData?.agent_id)) {
        throw new Error('Format d\'ID agent invalide');
      }

      if (appointmentData?.notary_id && !uuidRegex?.test(appointmentData?.notary_id)) {
        throw new Error('Format d\'ID notaire invalide');
      }

      const result = await appointmentService?.createAppointment(appointmentData);

      if (result?.error) {
        throw new Error(result?.error);
      }

      if (formData?.email) {
        const consultantName =
          selectedConsultant?.name ||
          selectedConsultant?.notary_name ||
          selectedConsultant?.user_profile?.full_name;

        sendAppointmentConfirmationEmail({
          client_name: `${formData?.firstName || ''} ${formData?.lastName || ''}`?.trim(),
          client_email: formData?.email,
          appointment_type: selectedService?.name,
          scheduled_date: selectedDateTime,
          duration_minutes: selectedService?.duration,
          location: appointmentData?.location,
          notary_name: selectedService?.id === 'legal_consultation' ? consultantName : undefined,
          consultant_name: selectedService?.id === 'property_consultation' ? consultantName : undefined,
        });
      }

      setCreatedAppointment(result?.data);

      if (result?.data?.id && formData?.email) {
        const consultantName =
          selectedConsultant?.name ||
          selectedConsultant?.notary_name ||
          selectedConsultant?.user_profile?.full_name;

        scheduleAppointmentReminders({
          appointment_id: result?.data?.id,
          client_name: `${formData?.firstName || ''} ${formData?.lastName || ''}`?.trim(),
          client_email: formData?.email,
          client_phone: formData?.phone || '',
          appointment_type: selectedService?.name,
          scheduled_date: selectedDateTime,
          notary_name: selectedService?.id === 'legal_consultation' ? consultantName : undefined,
          consultant_name: selectedService?.id === 'property_consultation' ? consultantName : undefined,
          location: appointmentData?.location,
        });
      }

      setBookingData({
        service: selectedService,
        consultant: selectedConsultant,
        dateTime: selectedDateTime,
        formData
      });
      setShowConfirmation(true);
    } catch (err) {
      setError(`Erreur lors de la création du rendez-vous: ${err?.message}`);
      console.error('Error creating appointment:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetBooking = () => {
    setCurrentStep(1);
    setSelectedService(null);
    setSelectedConsultant(null);
    setSelectedDateTime(null);
    setBookingData({});
    setShowConfirmation(false);
    setCreatedAppointment(null);
    setError(null);
  };

  // Progress bar width
  const progressPct = Math.round(((currentStep - 1) / (STEPS?.length - 1)) * 100);

  return (
    <>
      <Helmet>
        <title>Centre de Réservation de Consultations - Kapital States</title>
        <meta
          name="description"
          content="Réservez facilement vos consultations immobilières et légales. Accès unifié aux services de conseil et aux notaires."
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-16 sm:pt-20">
          {/* Hero */}
          <section className="bg-foreground text-background py-10 sm:py-14">
            <div className="max-w-4xl mx-auto px-4 lg:px-6 text-center">
              <p className="text-xs font-semibold tracking-widest uppercase text-muted mb-3 opacity-60">
                Kapital States
              </p>
              <h1 className="text-3xl sm:text-4xl font-heading font-bold mb-3 leading-tight">
                Centre de Réservation
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground/70 max-w-xl mx-auto">
                Accès unifié aux services de conseil immobilier et aux consultations légales
              </p>
            </div>
          </section>

          {/* Sticky stepper bar */}
          <div className="bg-card border-b border-border sticky top-16 sm:top-20 z-20">
            {/* Linear progress bar */}
            <div className="h-0.5 bg-border w-full">
              <div
                className="h-full bg-primary transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              />
            </div>

            <div className="max-w-2xl mx-auto px-4 py-4 sm:py-5">
              <Stepper currentStep={currentStep} steps={STEPS} />
            </div>
          </div>

          {/* Error banner */}
          {error && (
            <div className="max-w-4xl mx-auto px-4 pt-4">
              <div className="flex items-start gap-3 bg-destructive/10 border border-destructive/20 rounded-md px-4 py-3">
                <Icon name="AlertCircle" size={16} className="text-destructive mt-0.5 flex-shrink-0" />
                <p className="text-sm text-destructive flex-1">{error}</p>
                <button
                  onClick={() => setError(null)}
                  className="text-destructive/70 hover:text-destructive transition-colors"
                >
                  <Icon name="X" size={14} />
                </button>
              </div>
            </div>
          )}

          {/* Step content */}
          <div className="max-w-4xl mx-auto px-4 py-8 sm:py-10">
            {currentStep === 1 && (
              <ServiceSelector
                services={services}
                onServiceSelect={handleServiceSelect}
              />
            )}

            {currentStep === 2 && selectedService?.id === 'legal_consultation' ? (
              <IntelligentNotaryMatcher
                service={selectedService}
                onNotarySelect={handleConsultantSelect}
                onBack={() => setCurrentStep(1)}
              />
            ) : currentStep === 2 ? (
              <ConsultantMatcher
                service={selectedService}
                consultants={consultants}
                loading={loading}
                onConsultantSelect={handleConsultantSelect}
                onBack={() => setCurrentStep(1)}
              />
            ) : null}

            {currentStep === 3 && (
              <BookingCalendar
                service={selectedService}
                consultant={selectedConsultant}
                onDateTimeSelect={handleDateTimeSelect}
                onBack={() => setCurrentStep(2)}
              />
            )}

            {currentStep === 4 && (
              <BookingForm
                service={selectedService}
                consultant={selectedConsultant}
                dateTime={selectedDateTime}
                loading={loading}
                onSubmit={handleFormSubmit}
                onBack={() => setCurrentStep(3)}
              />
            )}
          </div>
        </main>

        {showConfirmation && (
          <PostBookingWorkflow
            appointment={createdAppointment}
            bookingData={bookingData}
            onClose={() => setShowConfirmation(false)}
            onNewBooking={resetBooking}
          />
        )}
      </div>
    </>
  );
};

export default ConsultationBookingHub;